import type * as api from '@gitbook/api-client';
import { Block, type Change, Fragment, Inline } from '@gitbook/slate';

import { createParagraph } from '../paragraph';
import {
    BLOCK_IMAGE,
    BLOCK_IMAGES,
    BLOCK_LIST_ITEM,
    BLOCK_TABS,
    BLOCK_TABS_ITEM,
    type BlockImageData,
    FRAGMENT_CAPTION,
    INLINE_IMAGE,
    type InlineImageData,
    type InlineImageSize,
} from '../types';
import { lengthToString } from '../utils';

/**
 * Create an images block.
 */
export function createImagesBlock(refs: Array<api.ContentRefURL | api.ContentRefFile>) {
    if (!refs.length) {
        throw new Error('cannot create images block with 0 images');
    }

    return Block.createVoid({
        type: BLOCK_IMAGES,
        nodes: refs.map((ref) => createImageBlock(ref)),
    });
}

/**
 * Create an image block.
 */
export function createImageBlock(
    ref: api.ContentRefURL | api.ContentRefFile,
    caption = '',
    alt = ''
) {
    return Block.createVoid({
        type: BLOCK_IMAGE,
        data: {
            alt,
            ref,
        },
        fragments: [
            Fragment.create({
                fragment: FRAGMENT_CAPTION,
                nodes: [createParagraph(caption)],
            }),
        ],
    });
}

/**
 * Create an image block.
 */
export function createImageInline(
    ref: api.ContentRefURL | api.ContentRefFile,
    args: {
        alt?: string;
        caption?: string;
        size?: InlineImageSize;
    } = { alt: '' }
) {
    return Inline.createVoid({
        type: INLINE_IMAGE,
        data: {
            ...args,
            ref,
        },
    });
}

/**
 * Replace an inline image by a block image.
 */
export function replaceInlineImageWithBlock(change: Change, inline: Inline) {
    const { document } = change.value;

    const inlineInListItem = document.getClosest(
        inline.key,
        (ancestor) => ancestor.object === 'block' && ancestor.type === BLOCK_LIST_ITEM
    );

    if (inlineInListItem) {
        const parent = inlineInListItem;
        if (parent.object !== 'block') {
            throw new Error(`Couldn't find parent listitem block for inline image "${inline.key}"`);
        }

        const index = parent.nodes.size; // The image block will be added at the end in the list item

        changeInlineImageWithBlock(change, inline, parent.key, index);
    } else {
        const parent = document.getFurthest(
            inline.key,
            (ancestor) =>
                ancestor.object === 'block' &&
                ancestor.type !== BLOCK_TABS &&
                ancestor.type !== BLOCK_TABS_ITEM
        );

        if (parent?.object !== 'block') {
            throw new Error(`Unable to find parent block for inline image "${inline.key}"`);
        }

        const grandParent = document.getParent(parent.key);
        if (!grandParent) {
            throw new Error(`Unable to find grandparent block for inline image "${inline.key}"`);
        }

        const index = grandParent.getChildIndex(parent.key);

        changeInlineImageWithBlock(change, inline, grandParent.key, index + 1, {
            parentKeyToRemoveIfEmpty: parent.key,
        });
    }
}

function changeInlineImageWithBlock(
    change: Change,
    inline: Inline,
    intoNodeKey: string,
    index: number,
    {
        parentKeyToRemoveIfEmpty,
    }: {
        /**
         * If the original parent *only* had an inline image, do we want to get rid of it after
         * creating the block image?
         */
        parentKeyToRemoveIfEmpty?: string;
    } = {}
) {
    // Create the new image
    const ref = inline.data.get('ref') as api.ContentRefURL | api.ContentRefFile;
    const block = Block.createVoid({
        type: BLOCK_IMAGES,
        nodes: [createImageBlock(ref)],
    });

    change.insertNodeByKey(intoNodeKey, index, block, {
        normalize: false,
    });
    change.removeNodeByKey(inline.key, {
        normalize: false,
    });

    const parent = parentKeyToRemoveIfEmpty
        ? (change.value.document.getByKey(parentKeyToRemoveIfEmpty) as Block)
        : null;
    if (parent?.isEmpty && parent.fragments.size === 0) {
        change.removeNodeByKey(parent.key, {
            normalize: false,
        });
    }

    change.moveToRangeOf(block);

    // Finally normalize the top level block
    change.normalizeNodeByKey(intoNodeKey);
}

/**
 * Utility method to get the alt text of an image node
 * For inline images, it returns either the alt text (stored directly) or the caption (which was previously used to store the alt text)
 * For block images, it returns the alt text stored in the data
 */
export function getImageAltText(node: Block | Inline): string | undefined {
    const imageData = node.data.toFrozenObject<InlineImageData | BlockImageData>();

    if ('alt' in imageData && typeof imageData.alt === 'string') {
        return imageData.alt;
    }

    // Previously caption on an inline node was used to store the alt text
    if (Inline.isInline(node) && 'caption' in imageData && typeof imageData.caption === 'string') {
        return imageData.caption;
    }

    return undefined;
}

/**
 * Get the image height from the `height` property of the node's data object
 */
export function getImageHeight(node: Block | Inline): string | undefined {
    const height = node.data.get('height');
    return height ? lengthToString(height) : undefined;
}

/**
 * Get the image width from the `width` property of the node's data object
 */
export function getImageWidth(node: Block | Inline): string | undefined {
    const width = node.data.get('width');
    return width ? lengthToString(width) : undefined;
}
